import { css } from '@emotion/core'
import React, { useCallback, useMemo } from 'react'
import { CheckboxList, ListItem, Divider } from '@fs/zion-ui'
import { NoticeInfo } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import {
  getPersonName,
  getPrimaryRelationshipText,
  getRelationshipToPrimaryPersonDisplayLabel,
  getRelationshipToPrimaryPersonText,
  useSlsStateContext,
} from '@fs/zion-sls'

const containerStyle = css`
  svg,
  span {
    pointer-events: none; // Need this to avoid the QuickGlance overlay to get the wrong target position
  }
`

const RecordGroup = ({
  record,
  onSelectionChange,
  onInfoIconClick,
  setPersonData,
  floatingRecords = false,
  showSeparator = false,
}) => {
  const slsState = useSlsStateContext()
  const [t] = useTranslation()

  // Filtering elements to get just the person objects and sorting them based on the primary flag
  const persons = useMemo(
    () =>
      floatingRecords
        ? (record ?? [])
        : ((record?.subElements?.filter((sub) => sub?.elementType === 'PERSON' && !sub.isDangling) ||
            record?.persons) ??
          []),
    [record, floatingRecords]
  )

  // Creating the array of data needed for showing the checkbox list for this record
  const checkboxData = useMemo(
    () =>
      persons?.length > 0
        ? persons.map((person) => {
            const personName = getPersonName({ person })
            const primaryRelationshipLabel = getRelationshipToPrimaryPersonDisplayLabel(person, slsState.types, record)
            const relationshipText = getRelationshipToPrimaryPersonText(person, slsState.types, record, t)
            const primaryRelationshipText = getPrimaryRelationshipText(primaryRelationshipLabel, relationshipText)

            return {
              overline: primaryRelationshipText,
              fullName: personName,
              ...person,
            }
          })
        : [],
    [persons, record, slsState.types, t]
  )

  // Function to handle the selection change on the checkbox list
  const handleChange = useCallback(
    (val) => {
      onSelectionChange({ [record.id || 'floating']: val })
    },
    [onSelectionChange, record.id]
  )

  const handleInfoButtonClick = ({ target }, person) => {
    setPersonData(person)
    onInfoIconClick({ target })
  }

  if (checkboxData && checkboxData.length === 0) return null

  return (
    <section css={containerStyle}>
      {floatingRecords && <Divider fullWidth />}
      {checkboxData && checkboxData.length > 0 && (
        <CheckboxList onSelectionChange={handleChange}>
          {checkboxData.map((person) => {
            return (
              person && (
                <ListItem
                  data-testid={`delete-${person.fullName}-list-item`}
                  key={person.id}
                  name={person.id}
                  overline={person.overline}
                  primaryText={person.fullName}
                  emphasized={!!person?.primary}
                  endElement={
                    <ListItem.IconButton
                      data-testid="removeItemInfoButton"
                      Icon={NoticeInfo}
                      color="gray"
                      onClick={(event) => handleInfoButtonClick(event, person)}
                    />
                  }
                />
              )
            )
          })}
        </CheckboxList>
      )}

      {showSeparator && <Divider fullWidth />}
    </section>
  )
}

export default RecordGroup
